import { useEffect } from 'react';
import { Check } from 'lucide-react';

/**
 * Ticket #62 — briques partagées des « modes outil » pilotés au clic droit
 * (saisie de temps et Planning) :
 *  - `buildModeCursor` : curseur flèche à pointe précise + badge du mode ;
 *  - `ToolModeContextMenu` : menu contextuel de choix du mode ;
 *  - `ToolModeBadge` : rappel du mode courant dans l'en-tête de la vue.
 *
 * Un mode est décrit par `{ key, label, Icon, color }`.
 */

// Flèche standard avec **pointe précise** (hotspot exactement sur le pixel
// cliqué, coin haut-gauche) + un badge coloré portant l'icône du mode en
// bas-droite. Les couleurs sont en dur : un data-URI SVG ne lit pas les
// variables CSS.
export function buildModeCursor(badgeIcon, color) {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>` +
    `<path d='M3 2 L3 22 L8.5 16.5 L12 23.5 L15 22 L11.5 15 L19 15 Z' ` +
    `fill='white' stroke='#1a1a1a' stroke-width='1.5' stroke-linejoin='round'/>` +
    `<circle cx='23' cy='23' r='8' fill='${color}' stroke='white' stroke-width='1.5'/>` +
    `<g stroke='white' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'>${badgeIcon}</g>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 2 2, pointer`;
}

/**
 * Menu contextuel de choix du mode. `position` = `{ x, y }` (coordonnées
 * viewport) ou null quand fermé. Se ferme au clic extérieur et sur Échap.
 */
export function ToolModeContextMenu({ position, modes, mode, onChange, onClose }) {
  useEffect(() => {
    if (!position) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('click', onClose);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClose);
      window.removeEventListener('keydown', onKey);
    };
  }, [position, onClose]);

  if (!position) return null;
  return (
    <div
      role="menu"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 1001,
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        padding: 4,
        minWidth: 170,
      }}
    >
      <div style={{ padding: '4px 10px', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Mode
      </div>
      {modes.map(({ key, label, Icon, color }) => (
        <button
          key={key}
          type="button"
          role="menuitemradio"
          aria-checked={mode === key}
          onClick={() => {
            onChange(key);
            onClose();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '7px 10px',
            background: mode === key ? 'var(--glass)' : 'transparent',
            color: 'var(--text)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Icon size={14} color={color} />
          {label}
          {mode === key ? <Check size={13} style={{ marginLeft: 'auto', color: 'var(--accent)' }} /> : null}
        </button>
      ))}
    </div>
  );
}

/** Pastille « Mode : … » affichée dans l'en-tête de la vue. */
export function ToolModeBadge({ modes, mode }) {
  const cur = modes.find((m) => m.key === mode) ?? modes[0];
  const CurIcon = cur.Icon;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 999,
        background: cur.color,
        color: '#fff',
        fontWeight: 600,
      }}
      title="Clic droit sur la grille pour changer de mode"
    >
      <CurIcon size={13} /> Mode : {cur.label}
    </span>
  );
}
